/**
 * The fill pill (20/10/2026): spinner and bar while filling, amber while
 * waiting for the AI or stalled, red once drafts failed twice — hover for
 * the first line, click for all of them and the button that acts: Resume
 * a stalled fill, Retry the drafts still without texts (both the same
 * idempotent fill: only drafts without texts are written).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { AlertTriangle, Loader2, PauseCircle, RotateCcw, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { useDropGeneratorMutations } from '../../../useDropGeneratorMutations';
import type { PillModel, PillTone } from './fill-words';

const TONE: Record<PillTone, string> = {
  filling: 'border-primary/25 bg-primary/5',
  waiting: 'border-warning/40 bg-warning/10',
  stalled: 'border-warning/50 bg-warning/10',
  failed: 'border-destructive/40 bg-destructive/10',
};
const ICON: Record<PillTone, FC<{ className?: string }>> = {
  filling: ({ className }) => <Loader2 className={cn(className, 'animate-spin text-primary')} />,
  waiting: ({ className }) => <PauseCircle className={cn(className, 'text-warning')} />,
  stalled: ({ className }) => <PauseCircle className={cn(className, 'text-warning')} />,
  failed: ({ className }) => <AlertTriangle className={cn(className, 'text-destructive')} />,
};
const ACTION_WORDS = { resume: 'Resume', retry: 'Retry failed', fill: 'Fill now' } as const;

interface Props {
  model: PillModel;
  countryKey: string;
  pct: number;
  onDismiss?: () => void;
}

export const FillPill: FC<Props> = ({ model, countryKey, pct, onDismiss }) => {
  const { startCreativeFill } = useDropGeneratorMutations();
  const { success, error } = useToast();
  const Icon = ICON[model.tone];
  const act = async () => {
    try {
      await startCreativeFill.mutateAsync(countryKey);
      success(model.action === 'resume' ? 'Fill resumed' : model.action === 'fill' ? 'Filling the drafts without texts' : 'Filling the drafts without texts again');
    } catch (err) {
      error('Could not start the fill', getApiErrorMessage(err));
    }
  };
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" title={model.lines[0]} aria-label={`${model.title} · ${model.count}`} data-tone={model.tone} data-testid="fill-pill" className={cn('inline-flex items-center gap-2 rounded-full border py-1 pl-2.5 pr-3 text-xs transition-colors hover:brightness-95', TONE[model.tone])}>
          <Icon className="h-3.5 w-3.5" />
          <span className="font-medium text-foreground">{model.title}</span>
          {model.tone === 'filling' || model.tone === 'waiting' ? (
            <span className="h-1 w-12 overflow-hidden rounded-full bg-primary/15">
              <span className="block h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${pct}%` }} />
            </span>
          ) : null}
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{model.count}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content sideOffset={6} align="end" className="z-50 flex w-80 flex-col gap-2 rounded-lg border bg-popover p-3 text-xs shadow-lg animate-in fade-in-0 zoom-in-95">
          <span className="flex items-center gap-2 font-semibold">
            {model.title}
            <span className="grow" />
            {onDismiss ? <button type="button" onClick={onDismiss} aria-label="Hide" className="rounded p-0.5 text-muted-foreground hover:bg-muted"><X className="h-3 w-3" /></button> : null}
          </span>
          <ul className="flex flex-col gap-1 leading-snug text-muted-foreground">
            {model.lines.map((line) => <li key={line}>{line}</li>)}
          </ul>
          {model.action ? (
            <Button size="sm" variant={model.action === 'resume' ? 'default' : 'outline'} className="h-7 self-start text-xs" disabled={startCreativeFill.isPending} onClick={() => void act()}>
              <RotateCcw className="mr-1 h-3 w-3" aria-hidden />
              {ACTION_WORDS[model.action]}
            </Button>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
